import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useEffect, useRef, useState } from 'react';
import { Animated, View } from 'react-native';
import { aiColors, tint } from '../../../../../ai-suite/theme';
import { LiveAreaBar } from '../../../functions/useCoachingEdge';

function colorOf(score: number) {
  return score >= 85
    ? aiColors.green
    : score >= 60
    ? aiColors.blue
    : aiColors.amber;
}

/** One area: its name, score and a bar that slides to it; +n when it moves. */
export const SkillBar = memo(
  ({ bar, dimmed }: { bar: LiveAreaBar; dimmed: boolean }) => {
    const { rpx } = useResponsiveContext();
    const [width, setWidth] = useState(0);
    const fill = useRef(new Animated.Value(0)).current;
    const badge = useRef(new Animated.Value(0)).current;
    useEffect(() => {
      Animated.spring(fill, {
        toValue: bar.score / 100,
        useNativeDriver: true,
        friction: 7,
      }).start();
    }, [bar.score, fill]);
    useEffect(() => {
      if (!bar.delta) return;
      badge.setValue(1);
      Animated.timing(badge, {
        toValue: 0,
        duration: 1800,
        delay: 600,
        useNativeDriver: true,
      }).start();
      // A new change, not a re-render with the same one, flashes the badge.
    }, [bar.area, bar.delta, bar.score, badge]);
    const color = colorOf(bar.score);
    return (
      <View style={{ gap: rpx(3), opacity: dimmed ? 0.45 : 1 }}>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <Typography.Text
            size={rpx(11)}
            color={aiColors.mutedForeground}
            numberOfLines={1}
            style={{ flex: 1 }}
          >
            {bar.name}
          </Typography.Text>
          {bar.delta ? (
            <Animated.View style={{ opacity: badge }}>
              <Typography.Text
                size={rpx(11)}
                bold
                color={bar.delta > 0 ? aiColors.green : aiColors.amber}
              >
                {bar.delta > 0 ? `+${bar.delta}` : `${bar.delta}`}
              </Typography.Text>
            </Animated.View>
          ) : null}
          <Typography.Text size={rpx(11)} bold color={aiColors.foreground}>
            {bar.score}
          </Typography.Text>
        </View>
        <View
          onLayout={event => setWidth(event.nativeEvent.layout.width)}
          style={{
            height: rpx(5),
            borderRadius: rpx(3),
            overflow: 'hidden',
            backgroundColor: tint(color, 0.15),
          }}
        >
          <Animated.View
            style={{
              height: '100%',
              borderRadius: rpx(3),
              backgroundColor: color,
              transform: [
                {
                  translateX: fill.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-width, 0],
                  }),
                },
              ],
            }}
          />
        </View>
      </View>
    );
  },
);
